import { Localization } from "@viamap/viamap2-common";
import { AnnotationQuality } from "./ANNTypes";

function normalizePages(pages: number[] | undefined): number[] {
   return Array.from(new Set(pages || []))
      .filter((page) => Number.isFinite(page) && page > 0)
      .map((page) => Math.trunc(page))
      .sort((a, b) => a - b);
}

function arePagesEqual(a: number[], b: number[]): boolean {
   const left = normalizePages(a);
   const right = normalizePages(b);
   return left.length === right.length && left.every((page, index) => page === right[index]);
}

export type ServitutDocumentSourceKind = "akt" | "file";
export type ServitutDocumentOriginKind = "ai" | "person";
export type ServitutReviewState = "unchecked" | "checked" | "rejected";
export type ServitutPageFindStatus = "found" | "none" | "not_run";
export type ServitutAiFieldStatus =
   | "not_run"
   | "cannot_run"
   | "ai_glad"
   | "ai_ikke_glad"
   | "person_approved";

export type ServitutAiFieldKey = "resume" | "entitledParty" | "condition" | "statement" | "pages";

export interface ANNServitutFieldStatusMap {
   resume?: ServitutAiFieldStatus;
   entitledParty?: ServitutAiFieldStatus;
   condition?: ServitutAiFieldStatus;
   statement?: ServitutAiFieldStatus;
   pages?: ServitutAiFieldStatus;
}

/** Compact provenance stored on the servitut document annotation. */
export interface ANNServitutProvenance {
   source?: ServitutDocumentSourceKind;
   origin?: ServitutDocumentOriginKind;
   reviewState?: ServitutReviewState;
   aiPagesKept?: number[];
   fieldStatus?: ANNServitutFieldStatusMap;
   analysisStale?: boolean;
}

export function emptyProvenance(): ANNServitutProvenance {
   return {
      reviewState: "unchecked",
      fieldStatus: {},
      analysisStale: false,
   };
}

export function mergeProvenance(
   previous: ANNServitutProvenance | undefined,
   patch: Partial<ANNServitutProvenance>,
): ANNServitutProvenance {
   return {
      ...emptyProvenance(),
      ...previous,
      ...patch,
      fieldStatus: {
         ...(previous?.fieldStatus || {}),
         ...(patch.fieldStatus || {}),
      },
   };
}

export function provenanceAfterDocumentChange(
   previous: ANNServitutProvenance | undefined,
   patch: Partial<ANNServitutProvenance>,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      ...patch,
      reviewState: "unchecked",
      analysisStale: true,
      fieldStatus: {
         resume: "not_run",
         entitledParty: "not_run",
         condition: "not_run",
         statement: "not_run",
         pages: patch.fieldStatus?.pages || "not_run",
      },
   });
}

export function provenanceAfterAiAnalysis(
   previous: ANNServitutProvenance | undefined,
   options: {
      aiPagesKept: number[];
      pageFindStatus: ServitutPageFindStatus;
      resumeStatus: ServitutAiFieldStatus;
      entitledPartyStatus: ServitutAiFieldStatus;
      conditionStatus: ServitutAiFieldStatus;
      statementStatus: ServitutAiFieldStatus;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      origin: "ai",
      reviewState: "unchecked",
      analysisStale: false,
      aiPagesKept: normalizePages(options.aiPagesKept),
      fieldStatus: {
         pages: options.pageFindStatus === "found"
            ? options.resumeStatus
            : options.pageFindStatus === "none"
               ? "ai_ikke_glad"
               : "not_run",
         resume: options.resumeStatus,
         entitledParty: options.entitledPartyStatus,
         condition: options.conditionStatus,
         statement: options.statementStatus,
      },
   });
}

export function provenanceAfterLocate(
   previous: ANNServitutProvenance | undefined,
   options: {
      aiPagesKept: number[];
      pageFindStatus: ServitutPageFindStatus;
      analysisStale?: boolean;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      origin: "ai",
      reviewState: "unchecked",
      analysisStale: options.analysisStale ?? false,
      aiPagesKept: normalizePages(options.aiPagesKept),
      fieldStatus: {
         pages: options.pageFindStatus === "found"
            ? "ai_glad"
            : options.pageFindStatus === "none"
               ? "ai_ikke_glad"
               : "not_run",
      },
   });
}

export function provenanceAfterContentAnalysis(
   previous: ANNServitutProvenance | undefined,
   options: {
      resumeStatus: ServitutAiFieldStatus;
      entitledPartyStatus: ServitutAiFieldStatus;
      conditionStatus: ServitutAiFieldStatus;
      statementStatus: ServitutAiFieldStatus;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: false,
      fieldStatus: {
         resume: options.resumeStatus,
         entitledParty: options.entitledPartyStatus,
         condition: options.conditionStatus,
         statement: options.statementStatus,
      },
   });
}

export function provenanceAfterPersonReview(
   previous: ANNServitutProvenance | undefined,
   reviewState: "checked" | "rejected",
   origin: ServitutDocumentOriginKind,
): ANNServitutProvenance {
   const personApproved: ANNServitutFieldStatusMap | undefined = reviewState === "checked"
      ? {
         resume: "person_approved",
         entitledParty: "person_approved",
         condition: "person_approved",
         statement: "person_approved",
         pages: "person_approved",
      }
      : previous?.fieldStatus;
   return mergeProvenance(previous, {
      reviewState,
      origin,
      fieldStatus: personApproved,
   });
}

export function provenanceAfterFieldAccept(
   previous: ANNServitutProvenance | undefined,
   field: Exclude<ServitutAiFieldKey, "pages">,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: false,
      fieldStatus: {
         [field]: "person_approved",
      },
   });
}

/** Undo a B-field accept without touching A page review. */
export function provenanceAfterFieldRevert(
   previous: ANNServitutProvenance | undefined,
   field: Exclude<ServitutAiFieldKey, "pages">,
   restoredStatus?: ServitutAiFieldStatus,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      fieldStatus: {
         [field]: restoredStatus || "not_run",
      },
   });
}

export function provenanceAfterDerivedInvalidation(
   previous: ANNServitutProvenance | undefined,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: true,
      fieldStatus: {
         resume: "not_run",
         entitledParty: "not_run",
         condition: "not_run",
         statement: "not_run",
         pages: previous?.fieldStatus?.pages,
      },
   });
}

/** Phase A page control: mark pages checked/rejected without approving B analysis fields. */
export function provenanceAfterPageReview(
   previous: ANNServitutProvenance | undefined,
   reviewState: "checked" | "rejected",
   origin: ServitutDocumentOriginKind,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      reviewState,
      origin,
      fieldStatus: {
         pages: reviewState === "checked" ? "person_approved" : previous?.fieldStatus?.pages,
      },
   });
}

export function historyCommentForProvenance(provenance: ANNServitutProvenance): string {
   const parts = [
      provenance.source === "file"
         ? Localization.getText("Loaded from file")
         : provenance.source === "akt"
            ? Localization.getText("Fetched from akt")
            : "",
      provenance.origin === "ai"
         ? Localization.getText("AI generated")
         : provenance.origin === "person"
            ? Localization.getText("Edited by person")
            : "",
      provenance.reviewState === "checked"
         ? Localization.getText("Checked")
         : provenance.reviewState === "rejected"
            ? Localization.getText("Rejected")
            : "",
   ].filter(Boolean);
   return parts.join(" · ");
}

export function qualityForAiStatus(status: ServitutAiFieldStatus | undefined): AnnotationQuality | undefined {
   if (status === "ai_glad" || status === "person_approved") {
      return AnnotationQuality.Gold;
   }
   if (status === "ai_ikke_glad") {
      return AnnotationQuality.Bronze;
   }
   return undefined;
}

export function statusFromConfidence(
   value: number | null | undefined,
   needsReview?: boolean,
   hasValue?: boolean,
   canRun = true,
): ServitutAiFieldStatus {
   if (!canRun) {
      return "cannot_run";
   }
   if (!hasValue && (value === null || value === undefined)) {
      return "not_run";
   }
   if (needsReview) {
      return "ai_ikke_glad";
   }
   if (value === null || value === undefined) {
      return hasValue ? "ai_glad" : "not_run";
   }
   const normalized = value > 1 ? value / 10 : value;
   if (normalized >= 0.85) {
      return "ai_glad";
   }
   return "ai_ikke_glad";
}

export function resolvePageFindStatus(options: {
   electronicDocument: boolean;
   hasAnalysis: boolean;
   pagesKept: number[];
}): ServitutPageFindStatus {
   if (options.electronicDocument) {
      return "found";
   }
   if (!options.hasAnalysis) {
      return "not_run";
   }
   return options.pagesKept.length > 0 ? "found" : "none";
}

export function areServitutPagesEqual(a: number[] | undefined, b: number[] | undefined): boolean {
   return arePagesEqual(a || [], b || []);
}

export function pagesDifferFromAi(selectedPages: number[], aiPagesKept: number[] | undefined, electronicDocument: boolean): boolean {
   if (electronicDocument) {
      return false;
   }
   if (!aiPagesKept || !aiPagesKept.length) {
      return selectedPages.length > 0;
   }
   return !arePagesEqual(selectedPages, aiPagesKept);
}

export function formatPageFindStatus(status: ServitutPageFindStatus): string {
   if (status === "found") {
      return Localization.getText("AI found pages");
   }
   if (status === "none") {
      return Localization.getText("AI found no pages");
   }
   return Localization.getText("Page search not run");
}

export function formatAiFieldStatus(status: ServitutAiFieldStatus | undefined): string {
   switch (status) {
      case "cannot_run":
         return Localization.getText("AI cannot run");
      case "ai_glad":
         return Localization.getText("AI confident");
      case "ai_ikke_glad":
         return Localization.getText("AI not confident");
      case "person_approved":
         return Localization.getText("Approved by person");
      case "not_run":
      default:
         return Localization.getText("AI not run");
   }
}

export function formatProvenanceChip(provenance: ANNServitutProvenance | undefined): string {
   const parts = [
      provenance?.source === "file"
         ? Localization.getText("Loaded from file")
         : provenance?.source === "akt"
            ? Localization.getText("Fetched from akt")
            : "",
      provenance?.origin === "ai"
         ? Localization.getText("AI generated")
         : provenance?.origin === "person"
            ? Localization.getText("Edited by person")
            : "",
      provenance?.reviewState === "checked"
         ? Localization.getText("Checked")
         : provenance?.reviewState === "rejected"
            ? Localization.getText("Rejected")
            : "",
      provenance?.analysisStale
         ? Localization.getText("Analysis outdated")
         : "",
   ].filter(Boolean);
   return parts.join(" · ");
}

export function provenanceChipTone(provenance: ANNServitutProvenance | undefined): "ok" | "warn" | "bad" | "neutral" {
   if (provenance?.reviewState === "rejected" || provenance?.analysisStale) {
      return "bad";
   }
   if (provenance?.reviewState === "checked") {
      return "ok";
   }
   if (provenance?.origin === "ai") {
      return "warn";
   }
   return "neutral";
}

export function aiFieldTone(status: ServitutAiFieldStatus | undefined): "ok" | "warn" | "bad" | "neutral" {
   switch (status) {
      case "ai_glad":
      case "person_approved":
         return "ok";
      case "ai_ikke_glad":
         return "warn";
      case "cannot_run":
         return "bad";
      default:
         return "neutral";
   }
}

export function normalizeDatoLoebenrKey(value?: string | null): string {
   return (value || "").replace(/[^0-9]/g, "");
}
